// The Performance Breakdown — "gap cockpit" (Direction 04·3).
// Each skill is a row: status pill · label · a 0–5 instrument bar with the manager's desired level shown
// as a draggable "bug" (the aviation reference marker) · a readout (actual /5 + signed gap). Rows are
// grouped by status (Needs attention / On target / Strengths) and sorted worst-first, so a time-poor
// reader sees where the gaps are without scanning eleven equal rows.
//
// This holds the editable Manager Scorecard state — the per-skill DESIRED minimum (expected_score), set
// by dragging the bug along its bar (1–5). Dragging repaints the row live; on release the row settles
// into the correct status group. Public API is unchanged from the old segmented-box version:
//   load / read / renderActual / clearActual / setLocked / setScorecardName / setOnChange.

import type { ManagerScorecard, SkillBreakdownRow, EvidenceState } from '../lib/types.js';
import { SKILL_ORDER, skillInfoById } from '../lib/skills.js';
import { setText } from '../lib/escape.js';

type Bucket = 'pending' | 'neg' | 'zero' | 'pos';

interface RowRefs {
  row: HTMLElement;
  pill: HTMLElement;
  bar: HTMLElement;
  fill: HTMLElement;
  bug: HTMLElement;
  bugCap: HTMLElement;
  readActual: HTMLElement;
  gap: HTMLElement;
}

interface Section {
  bucket: Exclude<Bucket, never>;
  wrap: HTMLElement;
  body: HTMLElement;
  count: HTMLElement;
  sort: (a: Row, b: Row) => number;
}

interface Row {
  id: string;
  gap: number;
}

export class PerformanceTable {
  private current: ManagerScorecard | null = null;
  private rows = new Map<string, RowRefs>();
  private lastActual = new Map<string, number | null>();
  // Per-skill evidence_state from the last Run. null = no Run yet (genuine "awaiting"). The cockpit used
  // to look only at actual_score (null for any unscored skill) and so labelled a NO_EVIDENCE miss the same
  // as a not-yet-run skill; tracking the state lets us tell "rep didn't demonstrate it" (Missing, penalised
  // — the engine already docks the score) apart from "can't score / not run yet" (neutral).
  private lastState = new Map<string, EvidenceState | null>();
  private sections = new Map<Bucket, Section>();
  private locked = false;
  private dragging = false;
  private onChange?: () => void;
  private onPillHover?: (id: string | null) => void;
  private scorecardName: string | null = null;

  constructor(private readonly root: HTMLElement) {}

  /** Hovering a status pill fires this with the skill id (and null on leave) so the host can show the
   *  matching coaching detail below the cockpit. */
  setOnPillHover(cb: (id: string | null) => void): void {
    this.onPillHover = cb;
  }

  private scorecardHeader(): string {
    if (!this.scorecardName) return 'Scorecard (system default)';
    const n = this.scorecardName;
    const shown = n.length > 12 ? n.slice(0, 12) + '…' : n;
    return `Scorecard (${shown})`;
  }

  /** Set the applied-template name. Retained for API compatibility; surfaced as a small chip on the rail
   *  when a template is applied (system default shows nothing). */
  setScorecardName(name: string | null): void {
    this.scorecardName = name && name.trim() ? name.trim() : null;
    const chip = this.root.querySelector('.ptc-scorecard');
    if (chip) {
      setText(chip as HTMLElement, this.scorecardName ? this.scorecardHeader() : '');
      (chip as HTMLElement).hidden = !this.scorecardName;
    }
  }

  /** Fires whenever the user drags a desired-minimum (drives live re-aggregation). */
  setOnChange(cb: () => void): void {
    this.onChange = cb;
  }

  load(sc: ManagerScorecard): void {
    this.current = structuredClone(sc);
    this.locked = false;
    this.render();
  }

  read(): ManagerScorecard | null {
    return this.current ? structuredClone(this.current) : null;
  }

  setLocked(locked: boolean): void {
    this.locked = locked;
    this.root.classList.toggle('perf-locked', locked);
  }

  clearActual(): void {
    for (const id of this.lastActual.keys()) this.lastActual.set(id, null);
    for (const id of this.lastState.keys()) this.lastState.set(id, null);
    this.regroup(true);
  }

  renderActual(breakdown: SkillBreakdownRow[]): void {
    // Join by skill_id, never by display name (the tight UI label legitimately differs from skill_name).
    const byId = new Map(breakdown.map((r) => [r.skill_id, r]));
    for (const { id } of SKILL_ORDER) {
      if (!this.rows.has(id)) continue;
      const r = byId.get(id);
      this.lastActual.set(id, r?.actual_score ?? null);
      // A skill in this run but absent from the breakdown is treated as unscored/awaiting (null state).
      this.lastState.set(id, r ? r.evidence_state : null);
    }
    // Don't reorder mid-drag: the row under the pointer must stay put until the gesture ends.
    this.regroup(!this.dragging);
  }

  // ---- rendering -------------------------------------------------------------------------------------

  private render(): void {
    const sc = this.current;
    this.root.className = 'pt-cockpit';
    this.root.innerHTML = '';
    this.rows.clear();
    this.lastActual.clear();
    this.lastState.clear();
    this.sections.clear();
    this.mountChrome();
    if (!sc) return;

    // 0–5 scale rail (shown once; faint integer gridlines repeat on every bar).
    const rail = div('ptc-rail');
    rail.append(spanCls('ptc-rail-cap', 'Level'));
    const scale = div('ptc-rail-scale');
    for (let v = 0; v <= 5; v++) {
      const t = spanCls('ptc-rt', String(v));
      t.style.left = `${(v / 5) * 100}%`;
      scale.appendChild(t);
    }
    rail.appendChild(scale);
    rail.append(spanCls('ptc-score-cap', 'Score'));
    rail.append(spanCls('ptc-gap-cap', 'Gap'));
    const chip = spanCls('ptc-scorecard', '');
    chip.hidden = true;
    rail.appendChild(chip);
    this.root.appendChild(rail);

    // Group sections (pending shows only before a run).
    const groupsWrap = div('ptc-groups');
    const defs: Array<{ bucket: Bucket; name: string; sort: (a: Row, b: Row) => number }> = [
      { bucket: 'pending', name: 'Awaiting scoring — run to score', sort: () => 0 },
      { bucket: 'neg', name: 'Gaps', sort: (a, b) => a.gap - b.gap },
      { bucket: 'zero', name: 'On target', sort: () => 0 },
      { bucket: 'pos', name: 'Strengths', sort: (a, b) => b.gap - a.gap },
    ];
    for (const d of defs) {
      const wrap = div(`ptc-group ptc-g-${d.bucket}`);
      const head = div('ptc-grp-h');
      // Label (tab + name + count) is capped to the status-pill column width so the category header lines
      // up with the pills stacked beneath it instead of overrunning them.
      const labelWrap = div('ptc-grp-label');
      labelWrap.append(spanCls('ptc-grp-tab', ''), spanCls('ptc-grp-name', d.name));
      const count = spanCls('ptc-grp-count', '0');
      // Count sits to the RIGHT of the capped label box, before the divider line.
      head.append(labelWrap, count, spanCls('ptc-grp-line', ''));
      const body = div('ptc-grp-body');
      wrap.append(head, body);
      groupsWrap.appendChild(wrap);
      this.sections.set(d.bucket, { bucket: d.bucket, wrap, body, count, sort: d.sort });
    }
    this.root.appendChild(groupsWrap);

    // One row per skill, created once and reused (moved between sections on regroup).
    for (const { id, label } of SKILL_ORDER) {
      const skill = sc.skills.find((s) => s.skill_id === id);
      if (!skill) continue;
      this.rows.set(id, this.buildRow(id, label, skill));
      this.lastActual.set(id, null);
      this.lastState.set(id, null);
    }
    this.regroup(true);
  }

  /** Corner brackets on the whole results card (score strip + breakdown), once (guarded). Static chrome,
   *  no data, no motion. */
  private mountChrome(): void {
    const card = this.root.closest('.results-b') ?? this.root.closest('.perf-inner');
    if (!card || card.querySelector('.ptc-corner')) return;
    for (const c of ['tl', 'tr', 'bl', 'br']) card.appendChild(div(`ptc-corner ptc-${c}`));
  }

  private buildRow(id: string, label: string, skill: ManagerScorecard['skills'][number]): RowRefs {
    const row = div('ptc-row');

    const pill = div('ptc-pill');
    // Hover the pill → surface the coaching detail for this skill below the cockpit.
    pill.addEventListener('mouseenter', () => this.onPillHover?.(id));
    pill.addEventListener('mouseleave', () => this.onPillHover?.(null));

    const name = div('ptc-name');
    setText(name, label);
    const info = skillInfoById(id);
    if (info) {
      name.appendChild(
        skillInfoIcon(`${skill.skill_name}: what it measures and why`, [
          `What it measures — ${info.measures}`,
          `Purpose — ${info.purpose}`,
        ]),
      );
    }

    // The instrument bar: track + integer gridlines + fill + the draggable target bug.
    const bar = div('ptc-bar');
    bar.setAttribute('role', 'slider');
    bar.setAttribute('aria-label', `${skill.skill_name} desired minimum`);
    bar.setAttribute('aria-valuemin', '1');
    bar.setAttribute('aria-valuemax', '5');
    bar.appendChild(div('ptc-track'));
    for (let i = 1; i <= 4; i++) {
      const seg = div('ptc-seg');
      seg.style.left = `${i * 20}%`;
      bar.appendChild(seg);
    }
    const fill = div('ptc-fill');
    fill.appendChild(div('ptc-fill-tip'));
    bar.appendChild(fill);
    const bug = div('ptc-bug');
    const bugCap = div('ptc-bug-cap');
    bug.append(bugCap, div('ptc-bug-arrow'), div('ptc-bug-line'));
    bar.appendChild(bug);
    this.wireBar(bar, skill);

    // Score (actual /5) and Gap are separate columns now — the gap reads on its own, under a "Gap" header.
    const scoreCell = div('ptc-score');
    const readActual = div('ptc-read-a');
    scoreCell.appendChild(readActual);
    const gapCell = div('ptc-gapcol');
    const gap = div('ptc-read-g');
    gapCell.appendChild(gap);

    row.append(pill, name, bar, scoreCell, gapCell);
    return { row, pill, bar, fill, bug, bugCap, readActual, gap };
  }

  /** Drag/click the bar to set the desired minimum (1–5). Live during drag (no reorder); settles into the
   *  right group on release. Inert while locked (busy / unidentified call). */
  private wireBar(bar: HTMLElement, skill: ManagerScorecard['skills'][number]): void {
    const setFromX = (clientX: number): void => {
      if (this.locked) return;
      const rect = bar.getBoundingClientRect();
      if (rect.width <= 0) return;
      // Level N is drawn at N/5 of the width (the bug sits on the tick), so map x with round — not
      // floor+1, which resolved a tick to the NEXT level and made the marker snap back on drag.
      const idx = Math.max(1, Math.min(5, Math.round(((clientX - rect.left) / rect.width) * 5)));
      bar.setAttribute('aria-valuenow', String(idx));
      if (idx === skill.expected_score) return;
      skill.expected_score = idx;
      this.paintRow(skill.skill_id); // live: bug + gap + readout follow the pointer
      this.onChange?.();
    };
    bar.addEventListener('pointerdown', (e) => {
      if (this.locked) return;
      this.dragging = true;
      try { bar.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      setFromX(e.clientX);
      e.preventDefault();
    });
    bar.addEventListener('pointermove', (e) => { if (this.dragging) setFromX(e.clientX); });
    const stop = (e: PointerEvent): void => {
      if (!this.dragging) return;
      this.dragging = false;
      try { bar.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      // Do NOT re-sort on edit: the row stays where it is so the manager can follow their own change.
      // The pill/gap already updated in place; groups re-sort on the next Run (renderActual).
    };
    bar.addEventListener('pointerup', stop);
    bar.addEventListener('pointercancel', stop);
    // Keyboard: arrows nudge the minimum.
    bar.tabIndex = 0;
    bar.addEventListener('keydown', (e) => {
      if (this.locked) return;
      let d = 0;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') d = 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') d = -1;
      else return;
      const next = Math.max(1, Math.min(5, skill.expected_score + d));
      e.preventDefault();
      if (next === skill.expected_score) return;
      skill.expected_score = next;
      this.paintRow(skill.skill_id); // update pill/gap/bug in place; no re-sort until the next Run
      this.onChange?.();
    });
  }

  // ---- painting --------------------------------------------------------------------------------------

  private statusOf(gap: number): Exclude<Bucket, 'pending'> {
    return gap > 0 ? 'pos' : gap === 0 ? 'zero' : 'neg';
  }
  private severity(gap: number): string {
    return gap > 0 ? 'pos' : gap === 0 ? 'zero' : gap === -1 ? 'amber' : 'neg';
  }

  /** Resolve a skill to its display state, using the last Run's evidence_state (not just its score). One
   *  place decides this so painting and grouping never disagree:
   *   - awaiting   : no Run yet for this skill (score genuinely pending) → pending group.
   *   - graded     : scored → normal gap math.
   *   - missing    : REQUIRED skill (expected ≥ 1) the rep didn't demonstrate (no_evidence). Counts as a
   *                  full miss (gap = −expected) in Needs attention — the engine already docks the score
   *                  (NO_EVIDENCE_ATTAINMENT); this just makes that penalty visible instead of hiding it
   *                  behind "Awaiting".
   *   - notscored  : can't-score-through-no-fault states (not_applicable / insufficient_transcript, or an
   *                  optional skill with no evidence) → neutral, not penalised. */
  private resolve(id: string): { bucket: Bucket; kind: 'awaiting' | 'graded' | 'missing' | 'notscored'; filled: number | null; gap: number } {
    const skill = this.current?.skills.find((s) => s.skill_id === id);
    if (!skill) return { bucket: 'pending', kind: 'awaiting', filled: null, gap: 0 };
    const expected = skill.expected_score;
    const actual = this.lastActual.get(id) ?? null;
    const state = this.lastState.get(id) ?? null;

    if (state === null) return { bucket: 'pending', kind: 'awaiting', filled: null, gap: 0 };
    if (state === 'scored' && actual !== null) {
      const filled = Math.round(actual);
      const gap = filled - expected;
      return { bucket: this.statusOf(gap), kind: 'graded', filled, gap };
    }
    if (state === 'no_evidence' && expected >= 1) {
      return { bucket: 'neg', kind: 'missing', filled: 0, gap: -expected };
    }
    return { bucket: 'pending', kind: 'notscored', filled: null, gap: 0 };
  }

  private paintRow(id: string): void {
    const refs = this.rows.get(id);
    const skill = this.current?.skills.find((s) => s.skill_id === id);
    if (!refs || !skill) return;
    const expected = skill.expected_score;

    // The target bug marks the manager's desired minimum. Shown for the whole 0–5 range, including 0:
    // an OPTIONAL skill reads clearer with an explicit "0" marker at the far-left than with no marker at
    // all (whose absence let the fill's own end-tip look like a mis-placed target). At 0 the marker pins
    // to the left edge (ptc-bug-zero) so its cap isn't clipped off the track.
    const showBug = expected >= 0 && expected <= 5;
    refs.bug.style.display = showBug ? '' : 'none';
    refs.bug.style.left = `${(expected / 5) * 100}%`;
    refs.bug.classList.toggle('ptc-bug-zero', expected === 0);
    setText(refs.bugCap, String(expected));

    const r = this.resolve(id);

    // Non-graded states share an empty bar + a status pill (no numeric /5 score).
    if (r.kind !== 'graded') {
      refs.fill.style.width = '0%';
      refs.readActual.innerHTML = '–<span class="ptc-of">/5</span>';
      if (r.kind === 'missing') {
        // A real miss: red row + red −gap so the (already-applied) penalty is visible, not hidden.
        refs.fill.className = 'ptc-fill sev-neg';
        refs.row.dataset.sev = 'neg';
        setText(refs.pill, 'Missing');
        refs.pill.className = 'ptc-pill sev-neg';
        setText(refs.gap, String(r.gap));
        refs.gap.className = 'ptc-read-g g-neg';
      } else {
        refs.fill.className = 'ptc-fill';
        refs.row.dataset.sev = 'pending';
        setText(refs.pill, r.kind === 'notscored' ? 'Not scored' : 'Awaiting');
        refs.pill.className = 'ptc-pill sev-pending';
        setText(refs.gap, '');
        refs.gap.className = 'ptc-read-g';
      }
      return;
    }
    const filled = r.filled as number;
    const gap = r.gap;
    const sev = this.severity(gap);
    refs.fill.style.width = `${(filled / 5) * 100}%`;
    refs.fill.className = `ptc-fill sev-${sev}`;
    refs.row.dataset.sev = sev;
    setText(refs.pill, gap < 0 ? (gap <= -2 ? 'Below' : 'Watch') : gap > 0 ? 'Above' : 'On target');
    refs.pill.className = `ptc-pill sev-${sev}`;
    refs.readActual.innerHTML = `${filled}<span class="ptc-of">/5</span>`;
    setText(refs.gap, gap > 0 ? `+${gap}` : String(gap));
    refs.gap.className = 'ptc-read-g ' + (gap < 0 ? 'g-neg' : gap > 0 ? 'g-pos' : 'g-zero');
  }

  /** Bucket every row by status, optionally reorder the DOM, and repaint. `reorder=false` during a drag so
   *  the grabbed row doesn't jump out from under the pointer. */
  private regroup(reorder: boolean): void {
    if (!this.current) return;
    const buckets: Record<Bucket, Row[]> = { pending: [], neg: [], zero: [], pos: [] };
    for (const { id } of SKILL_ORDER) {
      const skill = this.current.skills.find((s) => s.skill_id === id);
      if (!skill || !this.rows.has(id)) continue;
      const r = this.resolve(id); // one decision drives both the group and the paint
      buckets[r.bucket].push({ id, gap: r.gap });
    }
    for (const [bucket, section] of this.sections) {
      const items = buckets[bucket].slice().sort(section.sort);
      if (reorder) {
        for (const it of items) {
          const refs = this.rows.get(it.id);
          if (refs) section.body.appendChild(refs.row); // appendChild moves the existing node
        }
      }
      setText(section.count, String(items.length));
      section.wrap.hidden = items.length === 0;
    }
    for (const id of this.rows.keys()) this.paintRow(id);
  }
}

// ---- small DOM helpers -------------------------------------------------------------------------------

function div(cls: string): HTMLElement {
  const el = document.createElement('div');
  el.className = cls;
  return el;
}
function spanCls(cls: string, text: string): HTMLElement {
  const el = document.createElement('span');
  el.className = cls;
  setText(el, text);
  return el;
}

/** A compact inline (i) beside a skill label. Each line may lead with a bold "Label — " prefix (split on
 *  the first em dash). Reuses the shared .meta-info / .sc-note-tip hover styling; the tip is plain text. */
function skillInfoIcon(ariaLabel: string, lines: readonly string[]): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'meta-info pt-info';
  wrap.tabIndex = 0;
  wrap.setAttribute('role', 'note');
  wrap.setAttribute('aria-label', ariaLabel);
  const ico = document.createElement('span');
  ico.className = 'meta-info-ico';
  ico.setAttribute('aria-hidden', 'true');
  ico.textContent = 'i';
  const tip = document.createElement('span');
  tip.className = 'sc-note-tip';
  tip.setAttribute('role', 'tooltip');
  lines.forEach((line, i) => {
    if (i) tip.appendChild(document.createElement('br'));
    const dash = line.indexOf(' — ');
    if (dash > 0) {
      const strong = document.createElement('strong');
      setText(strong, line.slice(0, dash));
      tip.appendChild(strong);
      tip.appendChild(document.createTextNode(line.slice(dash)));
    } else {
      tip.appendChild(document.createTextNode(line));
    }
  });
  wrap.append(ico, tip);
  return wrap;
}
